/* eslint-disable no-empty */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const Services = ({
  serviceOptions,
  setServicesOptions,
  handleNext,
  setStep,
  qa_mode,
  auto_login = true,
  proxyUrl,
}) => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;
  const qa = useMemo(
    () => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode")),
    [qa_mode, params]
  );

  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const containerVariant = {
    hidden: { opacity: 0, x: 60, scale: 0.98 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.08,
        duration: 0,
        ease: [0.1, 1, 0.36, 1],
      },
    },
    exit: {
      opacity: 0,
      x: -60,
      scale: 0.96,
      transition: { duration: 0.2, ease: "easeInOut" },
    },
  };

  const answerVariant = {
    hidden: {
      opacity: 0,
      clipPath: "inset(0 100% 0 0)",
      transition: { duration: 0.4, ease: "easeOut" },
    },
    visible: {
      opacity: 1,
      clipPath: "inset(0 0% 0 0)",
      transition: { duration: 0.5, ease: "easeInOut" },
    },
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setServicesLoading(true);
      try {
        const { data } = await axios.post(
          PROXY_URL,
          { path: "/get-services", qa_mode: qa, method: "GET" },
          { headers: { "Content-Type": "application/json" } }
        );
        if (active) setServices(Array.isArray(data) ? data : []);
      } catch {
        if (active) setServices([]);
      } finally {
        if (active) setServicesLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [PROXY_URL, qa]);

  useEffect(() => {
    const ready = !servicesLoading && services.length > 0;
    if (ready) setShouldAnimate(true);
  }, [servicesLoading, services]);

  const handleAnswer = (answer) => {
    const answerStr = answer.toString();
    setServicesOptions((prev) =>
      prev.includes(answerStr)
        ? prev.filter((x) => x !== answerStr)
        : [...prev, answerStr]
    );
  };

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const CustomCheckbox = ({
    name,
    value,
    label,
    id,
    required = false,
  }) => {
    const isSelected = serviceOptions?.includes(value?.toString());

    return (
      <label
        className={`taxqa-option d-flex taxqa-checkbox ${
          isSelected ? "taxqa-selected" : ""
        }`}
        htmlFor={id}
      >
        <input
          type="checkbox"
          name={name}
          id={id}
          value={value}
          checked={isSelected}
          onChange={() => handleAnswer(value)}
          required={required}
          className="taxqa-input-hidden"
        />

        <span className="taxqa-check-icon">
          {isSelected ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="24" height="24" rx="4" fill="#4A4FFF" />
              <path
                d="M7 12.5L10.5 16L17 9"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="4"
                stroke="#4A4FFF"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M7 12.5L10.5 16L17 9"
                stroke="#4A4FFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>

        <span className="taxqa-option-label fs-5">{label}</span>
      </label>
    );
  };

  const nextDisabled = !(serviceOptions?.length > 0) || submitting;

  return (
    <div className="taxqa-card-wrapper">
      <motion.div
        key={`services-${services.length}-${servicesLoading}`}
        className="taxqa-card px-4 d-flex justify-content-center border-0"
        variants={containerVariant}
        initial="hidden"
        animate={shouldAnimate ? "visible" : "hidden"}
      >
        <div className="card-body mt-0 d-flex justify-content-center">
          <motion.div className="taxqa-inner">
            <motion.h2
              variants={answerVariant}
              className="taxqa-question-title"
            >
              What can we help you with?
            </motion.h2>

            <motion.p
              variants={answerVariant}
              className="taxqa-question-subtitle"
            >
              Choose as many that apply.
            </motion.p>

            {!servicesLoading && services.length === 0 && (
              <p className="text-muted text-center">No services available.</p>
            )}

            <div className="taxqa-options-group">
              {services.map((service) => (
                <motion.div
                  key={String(service.id)}
                  variants={answerVariant}
                  className="taxqa-option-row"
                >
                  <CustomCheckbox
                    name="services"
                    value={service.id}
                    label={service.name}
                    id={`service-${service.id}`}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="card-footer border-0  d-flex justify-content-between taxqa-footer">
          <div className="d-flex justify-content-between w-100 taxqa-footer-btns">
            <button className="taxqa-btn-secondary" onClick={handleBack}>
              <FiArrowLeft className="taxqa-btn-arrow" />
              Previous
            </button>
            <button
              className="taxqa-btn-primary"
              onClick={() => handleNext(false)}
              disabled={nextDisabled}
            >
              Next
              <FiArrowRight className="taxqa-btn-arrow" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Services;
